import {
  brauchtAbnahme,
  darfErledigen,
  TASK_STATUS_LABELS,
  type TaskSource,
  type TaskStatus,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { AufgabenVerweis } from '../AufgabeKontext'
import { useStatusSetzen } from '../../lib/mutationen'
import { Wiedersehen } from './Wiedersehen'
import { sitzungQuery, type Aufgabe } from '../../lib/queries'
import type { VorhabenSpur } from './lage'
import { Button, PrioBadge, SourceBadge } from '../ui/primitives'
import { AufgabenWeltMarke } from '../WorkspaceMarke'

/**
 * **Zone 1 – eine Karte, immer nur eine** ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * Vorher lagen hier bis zu acht gleich große Karten untereinander. Acht gleich große Karten
 * sind keine Antwort auf *„was mache ich jetzt"*, sondern dieselbe Frage in acht Teilen.
 *
 * Jetzt: die oberste Aufgabe groß, mit dem **Vorhaben als Herkunft** statt als Nebeninfo. Die
 * übrigen laufenden liegen als schmales Regal darunter und lassen sich mit einem Klick nach
 * vorn holen – die Reihenfolge ist eine Ansichtssache und ändert am Status nichts.
 */
export function Jetzt({
  laufend,
  wip,
  wipLimit,
  wipBereich,
  vorhabenInfo,
  nachVorn,
  vorhabenOeffnen,
  ziehen,
}: {
  laufend: readonly Aufgabe[]
  wip: number
  wipLimit: number
  /** „in deinen geteilten Welten“ oder „in <Welt>“ – woran die Zahl gemessen ist. */
  wipBereich: string
  /** Fortschritt und Bewegung je Vorhaben – für den Kopf der Behälter links. */
  vorhabenInfo?: Record<string, VorhabenSpur>
  nachVorn: (id: string) => void
  /** Derselbe Weg ins Vorhaben-Detail wie in Zone 2 ([E-205](../../../../../docs/ENTSCHEIDUNGEN.md)). */
  vorhabenOeffnen?: (id: string) => void
  ziehen: () => void
}) {
  const oben = laufend[0]

  /**
   * **Zwei Spalten, links das Vorhaben als Behälter** (Nils, 25.08.2026 – Variante 3 aus
   * [mockup-meintag-jetzt-spalten.html](../../../../../mockup-meintag-jetzt-spalten.html):
   * *„Nummer 3 ist mein absoluter Favorit."*).
   *
   * ## Die drei Schritte, die dahin geführt haben
   *
   * 1. **24.08.** – die Herkunft wurde eine Marke statt einer grauen Zeile. Das unterschied die
   *    Zeilen, ordnete sie aber nicht: bei vier laufenden Aufgaben stand die eine mit Vorhaben
   *    zwischen zweien ohne.
   * 2. **25.08. vormittags** – das Regal bekam zwei Gruppen untereinander. Besser, aber bei zehn
   *    Zeilen wird die Zone so hoch, dass „Auf dem Schirm" darunter verschwindet.
   * 3. **Jetzt** – nebeneinander. Links, was ein Vorhaben voranschiebt, rechts das Lose.
   *
   * ## Warum links Behälter und rechts eine flache Liste
   *
   * Weil die beiden Seiten verschiedene Fragen beantworten. Rechts: *was liegt sonst noch offen* –
   * eine Aufzählung, mehr ist nicht zu sagen. Links: *welches Vorhaben schiebe ich gerade* – und
   * dort ist die Aufgabe nicht der Gegenstand, sondern das Vorhaben. Deshalb trägt der Behälter
   * den Fortschritt und die Bewegung, dieselbe Auskunft wie die Karte in Zone 2.
   *
   * Der Preis, den Nils gesehen und getragen hat: bei je einer Aufgabe pro Vorhaben ist der
   * Behälter größer als sein Inhalt.
   *
   * ## Was die große Karte damit nicht zu tun hat
   *
   * Sie steht über beiden Spalten und in keinem Behälter. Sie beantwortet *woran arbeite ich
   * gerade*, und das ist keine Frage der Herkunft. Ein Vorhaben, dessen einzige laufende Aufgabe
   * die große Karte ist, bekommt links **keinen Behälter** – ein leerer Behälter wäre eine
   * Zeile, die nichts sagt. Seit dem 25.08.2026 steht dort stattdessen ein Satz („Steht oben in
   * der großen Karte"), damit die Spalte nicht zusammenfällt und die rechte Liste nach links
   * rutscht; warum das wichtig ist, steht unten bei `getrennt`.
   */
  const rest = laufend.slice(1)
  const ohneVorhaben = rest.filter((a) => !a.vorhabenLabel)

  /**
   * Je Vorhaben ein Behälter, in der Reihenfolge, in der die Aufgaben stehen – wer zuerst
   * auftaucht, steht oben. Kein `localeCompare` mehr: seit es Behälter sind, ordnet die
   * Reihenfolge Vorhaben und nicht Titel, und alphabetisch wäre dafür eine Aussage über nichts.
   */
  const behaelter: { id: string; label: string; info?: VorhabenSpur; zeilen: Aufgabe[] }[] = []
  for (const a of rest) {
    if (!a.vorhabenLabel) continue
    const id = a.vorhabenId ?? a.vorhabenLabel
    const vorhandener = behaelter.find((b) => b.id === id)
    if (vorhandener) vorhandener.zeilen.push(a)
    else
      behaelter.push({
        id,
        label: a.vorhabenLabel,
        ...(a.vorhabenId && vorhabenInfo?.[a.vorhabenId]
          ? { info: vorhabenInfo[a.vorhabenId] }
          : {}),
        zeilen: [a],
      })
  }

  const mitVorhabenAnzahl = rest.length - ohneVorhaben.length
  /**
   * **Spalten nur, wenn es etwas zu trennen gibt – gemessen an der ganzen Zone.**
   *
   * Liegt alles im selben Fach, wäre eine zweite Spalte leer und die Überschrift darüber eine
   * Auskunft ohne Gegenstück. Für jemanden, der überwiegend Tickets macht, stünde dann jeden Tag
   * „Einzelaufgaben" über einer Liste, neben der nichts steht. „Ohne Vorhaben ist ein Fach, kein
   * Mangel" (24.08.2026) gilt weiter.
   *
   * ## Warum `laufend` und nicht `rest` (25.08.2026)
   *
   * Bis heute zählte diese Regel nur die Zeilen **unter** der großen Karte. Das klang richtig –
   * die Karte steht ja in keiner Spalte – und hatte eine Folge, die Nils für einen Fehler
   * hielt:
   *
   * > *„Oh die Ansicht ist wieder die alte bei «JETZT», hattest du die wieder mit der alten
   * > überschrieben?"*
   *
   * Überschrieben war nichts. Seine einzige Vorhaben-Aufgabe war gerade die große Karte, unten
   * standen nur Einzelaufgaben – also nichts zu trennen, also keine Spalten. Ein Klick auf
   * **„nach vorn"** bei einer beliebigen Zeile hätte sie zurückgeholt: die Vorhaben-Aufgabe
   * rutscht nach unten, und plötzlich steht die Ansicht wieder anders da.
   *
   * **Eine Handlung, die nur die Reihenfolge ändert, darf nicht die Form ändern.** Deshalb
   * zählt jetzt die ganze Zone. Die große Karte bleibt dabei über den Spalten und in keinem
   * Behälter – daran ändert sich nichts; sie entscheidet nur nicht mehr mit darüber, ob es
   * Spalten *gibt*.
   *
   * Der Preis ist ein Fall, den es vorher nicht gab: ist die einzige Vorhaben-Aufgabe die große
   * Karte, steht links eine Überschrift ohne Behälter darunter. Das ist der kleinere Schaden –
   * eine ruhige Anordnung, die man versteht, gegen eine, die bei jedem Klick springt.
   */
  const getrennt = laufend.some((a) => a.vorhabenLabel) && laufend.some((a) => !a.vorhabenLabel)
  const ohneLimit = !Number.isFinite(wipLimit)
  /** Der Platz zählt über beide Spalten weiter – links zuerst, wie man liest. */
  const platzRechts = mitVorhabenAnzahl + 2

  return (
    /**
     * **Die Zone bekommt einen eigenen Rahmen** (Nils, 24.08.2026: *„Der Bereich, der aktiv in
     * Arbeit ist, muss noch etwas mehr hervorgehoben werden. Wenn man das System das erste Mal
     * öffnet, kommt man damit noch nicht so auf Anhieb klar."*).
     *
     * Vorher stand hier dieselbe Überschrift im selben Grau wie „Geschafft" und „Ruhig
     * gestellt" – fünf gleich aussehende Abschnitte untereinander, und der wichtigste hob sich
     * nur dadurch ab, dass er oben stand. Jetzt umschließt eine eigene Fläche mit Akzentkante
     * die ganze Zone, und die Überschrift sagt in Worten, was sie zeigt.
     */
    <section className="rounded-card border border-line2 border-l-[3px] border-l-accent bg-accent-soft/40 p-3.5">
      <div className="mb-[9px] flex flex-wrap items-baseline gap-x-[9px] gap-y-1">
        {/*
          Die Überschrift heißt genau „Jetzt" und nicht „Jetzt in Arbeit": mehrere Tests der
          Schirm-Zone benutzen sie als Anker dafür, dass „Mein Tag" überhaupt gerendert hat.
          Die Erklärung trägt ohnehin die Zeile daneben, und die Hervorhebung der Rahmen.
        */}
        <h3 className="text-[13px] font-bold tracking-[0.06em] text-accent-deep uppercase">
          Jetzt
        </h3>
        {/**
         * **Ohne Limit keine Plätze** (Z-07.13, [E-238](../../../../../docs/ENTSCHEIDUNGEN.md#e-238)).
         *
         * Im persönlichen Workspace ohne eigenes Limit ist `wipLimit` `Infinity` – eigene Todos
         * sind keine angefangene Abteilungsarbeit. „2 von Infinity Plätzen" wäre Unsinn, und
         * `Array.from({ length: Infinity })` wirft. Also sagt die Zeile, was gilt: nichts.
         */}
        {ohneLimit ? (
          <span className="text-xs text-ink2">
            woran du gerade arbeitest · <b>{wip} laufen</b> – hier zählt kein Limit
          </span>
        ) : (
          <>
            {/**
             * **Der Bereich steht immer dabei** (Nils, 02.10.2026, bei der Abnahme von Z-19).
             *
             * Hier stand „0 von 7 Plätzen" über drei sichtbar laufenden Karten. Nach der Regel
             * stimmte das – die drei lagen in persönlichen Welten, und die zählen nicht mit
             * ([E-238](../../../../../docs/ENTSCHEIDUNGEN.md#e-238)). Gelesen hat es sich wie ein
             * Widerspruch, und eine Zahl, die man nicht mit dem zusammenbringt, was darunter
             * steht, ist schlimmer als keine.
             *
             * `wipHinweisText` sagte das Gleiche schon ausführlich – aber nur, **wenn der Fokus
             * voll ist**. Bei 0 von 7 erschien es nie, und genau dort entsteht die Verwirrung.
             */}
            <span className="text-xs text-ink2">
              woran du gerade arbeitest ·{' '}
              <b className={wip > wipLimit ? 'text-pool-sofort' : 'text-ink2'}>
                {wip} von {wipLimit} {wipLimit === 1 ? 'Platz' : 'Plätzen'}
              </b>{' '}
              {wipBereich}
              {wip > wipLimit ? ' – über dem Limit' : wip === wipLimit ? ' – voll' : ''}
            </span>
            {/**
             * **Das WIP-Limit als Steckplätze statt als Zahl** – Mechanik 03 aus dem Entwurf.
             * „WIP 2 / 3" liest man weg; drei Balken, von denen zwei voll sind, sieht man. Die
             * Regel dahinter ist unverändert `darfZiehen`.
             */}
            <span className="ml-auto flex items-center gap-1" title={`WIP ${wip} / ${wipLimit}`}>
              {Array.from({ length: Math.max(wipLimit, wip) }, (_, i) => (
                <i
                  key={i}
                  className={`block h-[5px] w-3.5 rounded-sm ${
                    i >= wipLimit ? 'bg-pool-sofort' : i < wip ? 'bg-accent' : 'bg-line2'
                  }`}
                />
              ))}
            </span>
          </>
        )}
      </div>

      {oben ? <GrosseKarte aufgabe={oben} /> : <Leer ziehen={ziehen} />}

      {rest.length > 0 && (
        <div
          className={`mt-2.5 grid items-start gap-3 ${getrennt ? 'lg:grid-cols-2' : 'grid-cols-1'}`}
        >
          {/**
           * **Die linke Spalte steht auch leer da, solange getrennt wird.**
           *
           * Sonst rutschte die rechte Liste in den linken Platz, und aus zwei Spalten würde
           * eine halbe Seite Text neben einer halben Seite Nichts – genau das Springen, das
           * diese Änderung abstellen soll. Der Fall tritt ein, wenn die einzige
           * Vorhaben-Aufgabe gerade die große Karte ist; dann sagt die Spalte das auch.
           */}
          {(behaelter.length > 0 || getrennt) && (
            <div>
              {getrennt && <Spaltentitel titel="◆ An einem Vorhaben" anzahl={mitVorhabenAnzahl} />}
              {behaelter.length === 0 && (
                <p className="rounded-xl border border-dashed border-line px-3 py-2.5 text-[12.5px] text-muted">
                  Steht oben in der großen Karte.
                </p>
              )}
              {behaelter.map((b, i) => {
                const vorher = behaelter.slice(0, i).reduce((n, x) => n + x.zeilen.length, 0)
                return (
                  <VorhabenBehaelter
                    key={b.id}
                    behaelter={b}
                    abPlatz={vorher + 2}
                    nachVorn={nachVorn}
                    {...(vorhabenOeffnen ? { oeffnen: vorhabenOeffnen } : {})}
                  />
                )
              })}
            </div>
          )}

          {ohneVorhaben.length > 0 && (
            <div>
              {getrennt && <Spaltentitel titel="◇ Einzelaufgaben" anzahl={ohneVorhaben.length} />}
              <div className="overflow-hidden rounded-xl border border-line">
                {ohneVorhaben.map((a, i) => (
                  <Regalzeile
                    key={a.id}
                    aufgabe={a}
                    platz={(getrennt ? platzRechts : 2) + i}
                    /**
                     * **Ohne Spalten trägt die Zeile die Marke wieder.** Steht die Überschrift
                     * „Einzelaufgaben" darüber, wäre „◇ ohne Vorhaben" an jeder Zeile dasselbe
                     * zweimal.
                     */
                    markeZeigen={!getrennt}
                    nachVorn={() => nachVorn(a.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function Leer({ ziehen }: { ziehen: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-card border border-line2 bg-surface px-5 py-[18px] shadow-card">
      <div className="flex-1">
        <h4 className="text-[17px] font-semibold">Nichts läuft gerade.</h4>
        <p className="mt-0.5 text-[13px] text-muted">
          Alle Plätze frei. Work hat einen Vorschlag, wenn du willst.
        </p>
      </div>
      <Button variant="primary" onClick={ziehen}>
        Loslegen
      </Button>
    </div>
  )
}

function GrosseKarte({ aufgabe }: { aufgabe: Aufgabe }) {
  const status = useStatusSetzen()
  const [wiedersehenOffen, setWiedersehenOffen] = useState(false)
  const { data: sitzung } = useQuery(sitzungQuery)

  const abnahme = brauchtAbnahme(aufgabe, sitzung?.id)
  const kannErledigen = darfErledigen(aufgabe, sitzung?.id)
  const seit = aufgabe.workTakenAt ? seitWann(new Date(aufgabe.workTakenAt)) : null

  return (
    /* `data-jetzt` ist ein Haken für die Tests: die große Karte über eine CSS-Klasse zu
       suchen bricht bei jeder Layout-Änderung, und dann prüft der Test nichts mehr. */
    <div
      data-jetzt={aufgabe.id}
      className="relative overflow-hidden rounded-card border border-line2 bg-surface px-5 py-[18px] pl-[23px] shadow-card"
    >
      <span className="absolute inset-y-0 left-0 w-[3px] bg-accent" aria-hidden="true" />

      {/**
       * **Die Herkunft steht über dem Titel, nicht darunter.** Ein Vorhaben ist der Behälter
       * der Arbeit; wer es als Nebeninfo an den Rand setzt, macht aus der Hauptspur eine
       * Fußnote. „Ohne Vorhaben" steht genauso da – als Fach, nicht als Mangel.
       */}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted">
        {/**
         * **Das Vorhaben ist eine Marke, keine graue Zeile** (Nils, 24.08.2026: *„schön wäre,
         * wenn man auch in JETZT sieht, was Vorhaben sind und was nicht"*).
         *
         * Vorher stand hier farbiger Text in derselben Größe wie „seit 2 Stunden" – die
         * Herkunft sah aus wie eine Nebenangabe. Sie ist aber die Hauptspur: dieselbe Marke
         * wie auf der Vorhaben-Karte in Zone 2, damit man beides als dasselbe erkennt.
         *
         * **„Ohne Vorhaben" bleibt still.** Es ist ein Fach, kein Mangel – wer überwiegend
         * Tickets macht, soll nicht bei jeder Aufgabe an eine Lücke erinnert werden.
         */}
        {aufgabe.vorhabenLabel ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-vorhaben-bg px-2 py-[3px] text-[12px] font-semibold text-pool-projekte">
            ◆ {aufgabe.vorhabenLabel}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-[3px] text-[11.5px]">
            ◇ Ohne Vorhaben
          </span>
        )}
        {seit && <span>seit {seit}</span>}
      </div>

      <AufgabenVerweis
        aufgabeId={aufgabe.id}
        className="mb-2.5 block cursor-pointer text-left text-xl font-semibold tracking-[-0.3px] hover:text-accent hover:underline"
      >
        {aufgabe.title}
      </AufgabenVerweis>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
        <SourceBadge source={aufgabe.sourceId as TaskSource} label={aufgabe.externalKey} />
        {/**
         * **Aus welcher Arbeitswelt das kommt** (Z-07.8). „Mein Tag" mischt sie bewusst – ein
         * Mensch hat einen Tag (E-234) –, und genau deshalb braucht die Zeile den Hinweis:
         * „Überweisung machen" neben „IOR 2.0" sind zwei Welten, und man sieht es sonst nicht.
         */}
        <AufgabenWeltMarke workspaceId={aufgabe.workspaceId} />
        {aufgabe.priority && <PrioBadge priority={aufgabe.priority} />}
        {aufgabe.externalUrl && (
          <a
            href={aufgabe.externalUrl}
            target="_blank"
            rel="noreferrer"
            className="text-accent-deep hover:underline"
          >
            Quelle öffnen ↗
          </a>
        )}
      </div>

      {/**
       * **Hier stehen die Knöpfe, sonst nirgends.** Auf der aktiven Karte ist eine Bedienleiste
       * richtig – auf zehn Karten wäre sie Lärm. Was nicht geht, steht nicht da: braucht die
       * Aufgabe eine Abnahme, fällt „Erledigt" weg (`darfErledigen`, dieselbe Regel wie im
       * Server).
       */}
      {/**
       * **Auf dem Telefon ist „Erledigt" ein Daumenknopf** (Z-16): volle Breite, hoch, groß –
       * es ist dort die häufigste Handlung an der laufenden Aufgabe. Die beiden anderen teilen
       * sich die Zeile darunter. Ab `sm:` die Leiste wie bisher.
       */}
      <div className="mt-4 flex flex-wrap gap-2">
        {kannErledigen && (
          <Button
            variant="primary"
            disabled={status.isPending}
            onClick={() => status.mutate({ id: aufgabe.id, status: 'erledigt' as TaskStatus })}
            className="max-sm:basis-full max-sm:py-3 max-sm:text-[15px]"
          >
            ✓ Erledigt
          </Button>
        )}
        <Button
          variant={kannErledigen ? 'ghost' : 'primary'}
          disabled={status.isPending}
          className="max-sm:flex-1"
          onClick={() => status.mutate({ id: aufgabe.id, status: 'review' as TaskStatus })}
          title={
            abnahme && aufgabe.abnahmeDurchName
              ? `Auf „Review/Abnahme" setzen – ${aufgabe.abnahmeDurchName} entscheidet dann`
              : 'Auf „Review/Abnahme" setzen'
          }
        >
          {abnahme && aufgabe.abnahmeDurchName
            ? `Zur Abnahme (${aufgabe.abnahmeDurchName})`
            : TASK_STATUS_LABELS.review}
        </Button>
        {/**
         * **Zurücklegen fragt nach dem Wiedersehen** (Z-19 AP 19.10). Vorher gab dieser Knopf
         * die Aufgabe sofort in den Pool zurück – eine von zwei möglichen Antworten, stillschweigend
         * gewählt. Wer sie später selbst machen wollte, fand den Weg nur über das Statusmenü und
         * hat deshalb lieber gar nichts zurückgelegt.
         */}
        <Button
          variant="ghost"
          className="max-sm:flex-1"
          onClick={() => setWiedersehenOffen(true)}
          title="Später selbst, oder zurück in den Pool – ohne Antwort geht sie nicht hinaus"
        >
          ← zurücklegen
        </Button>
        {wiedersehenOffen && (
          <Wiedersehen
            aufgabeId={aufgabe.id}
            titel={aufgabe.title}
            schliessen={() => setWiedersehenOffen(false)}
          />
        )}
      </div>
    </div>
  )
}

/** Die Überschrift einer Spalte – nur da, wenn es zwei gibt. */
function Spaltentitel({ titel, anzahl }: { titel: string; anzahl: number }) {
  return (
    <div className="mb-1.5 flex items-baseline gap-2 px-0.5">
      <span className="text-[10px] font-semibold tracking-[0.08em] text-ink2 uppercase">
        {titel}
      </span>
      <span className="tabular text-[10.5px] text-muted">
        {anzahl === 1 ? '1 Aufgabe' : `${anzahl} Aufgaben`}
      </span>
    </div>
  )
}

/**
 * **Ein Vorhaben mit seinen laufenden Aufgaben** – dieselbe Form wie die Karte in Zone 2, nur
 * schmaler.
 *
 * Der Kopf trägt **Fortschritt und Bewegung**, weil die Frage hier nicht *welche Aufgabe* lautet,
 * sondern *welches Vorhaben schiebe ich gerade voran*. Fehlt die Auskunft (das Vorhaben steht
 * nicht in `vorhaben.alle`, etwa weil es stillgelegt wurde), bleibt der Name allein stehen –
 * lieber eine Angabe weniger als eine erfundene.
 *
 * Die Zeilen darin tragen **keine** Marke mehr: der Kopf sagt bereits, zu welchem Vorhaben sie
 * gehören.
 */
function VorhabenBehaelter({
  behaelter,
  abPlatz,
  nachVorn,
  oeffnen,
}: {
  behaelter: { id: string; label: string; info?: VorhabenSpur; zeilen: Aufgabe[] }
  abPlatz: number
  nachVorn: (id: string) => void
  oeffnen?: (id: string) => void
}) {
  const info = behaelter.info
  return (
    <article className="mb-2 overflow-hidden rounded-xl border border-line bg-surface last:mb-0">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line bg-vorhaben-bg px-3 py-1.5">
        {oeffnen ? (
          <button
            type="button"
            onClick={() => oeffnen(behaelter.id)}
            className="min-w-0 cursor-pointer truncate text-[12px] font-semibold text-pool-projekte hover:underline"
            title={behaelter.label}
          >
            ◆ {behaelter.label}
          </button>
        ) : (
          <span className="min-w-0 truncate text-[12px] font-semibold text-pool-projekte">
            ◆ {behaelter.label}
          </span>
        )}
        {info && (
          <span className="tabular ml-auto shrink-0 text-[10.5px] text-muted">
            {info.stand.erledigt} von {info.stand.gesamt} · <Bewegung spur={info} />
          </span>
        )}
      </div>
      {behaelter.zeilen.map((a, i) => (
        <Regalzeile
          key={a.id}
          aufgabe={a}
          platz={abPlatz + i}
          markeZeigen={false}
          nachVorn={() => nachVorn(a.id)}
        />
      ))}
    </article>
  )
}

/**
 * **Bewegt es sich?** – die ehrlichste Kennzahl für ein Vorhaben (F-62), hier in einer Zeile.
 *
 * Bewusst ohne Alarmfarbe außer bei „steht": in Zone 1 stehen die Vorhaben, an denen gerade
 * gearbeitet wird. Ein rotes Wort neben laufender Arbeit wäre ein Vorwurf für etwas, das man in
 * derselben Sekunde tut.
 */
function Bewegung({ spur }: { spur: VorhabenSpur }) {
  if (spur.zustand === 'steht') {
    return <b className="font-semibold text-warn">steht {spur.bewegtSeitTagen} T.</b>
  }
  if (spur.zustand === 'bewegt') {
    return <span>bewegt sich</span>
  }
  if (spur.zustand === 'unbekannt') return <span>ohne Bewegung</span>
  return <span>vor {spur.bewegtSeitTagen} T.</span>
}

function Regalzeile({
  aufgabe,
  platz,
  markeZeigen,
  nachVorn,
}: {
  aufgabe: Aufgabe
  platz: number
  /**
   * Steht die Herkunft an der Zeile? **Nein**, sobald eine Überschrift oder ein Behälter sie
   * schon nennt – sonst stünde dasselbe zweimal in derselben Zeile.
   */
  markeZeigen: boolean
  nachVorn: () => void
}) {
  return (
    <div className="flex items-center gap-2.5 bg-surface px-3.5 py-2 text-[13px] text-ink2 not-first:border-t not-first:border-line">
      <span className="shrink-0 rounded-md border border-line bg-page px-[7px] py-[2px] text-[10.5px]">
        Platz {platz}
      </span>
      <AufgabenVerweis
        aufgabeId={aufgabe.id}
        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
        title={aufgabe.title}
      >
        {aufgabe.title}
      </AufgabenVerweis>
      {/* Dieselbe Marke wie oben – im Regal nur kleiner. Ohne Vorhaben steht ebenfalls da:
          sonst weiß man bei einer unmarkierten Zeile nicht, ob die Angabe fehlt oder ob es
          keine gibt. */}
      {markeZeigen &&
        (aufgabe.vorhabenLabel ? (
          <span
            className="shrink-0 truncate rounded-md bg-vorhaben-bg px-[7px] py-[2px] text-[10.5px] font-semibold text-pool-projekte"
            title={aufgabe.vorhabenLabel}
          >
            ◆ {aufgabe.vorhabenLabel}
          </span>
        ) : (
          <span className="shrink-0 rounded-md border border-line px-[7px] py-[2px] text-[10.5px] text-muted">
            ◇ ohne Vorhaben
          </span>
        ))}
      <Button size="sm" variant="ghost" onClick={nachVorn}>
        nach vorn
      </Button>
    </div>
  )
}

/** „seit 1 h 12 min", „seit gestern" – grob genug, dass es nicht bei jedem Blick springt. */
function seitWann(seit: Date): string {
  const minuten = Math.floor((Date.now() - seit.getTime()) / 60_000)
  if (minuten < 60) return `${Math.max(1, minuten)} min`
  const stunden = Math.floor(minuten / 60)
  if (stunden < 24) return `${stunden} h ${minuten % 60} min`
  const tage = Math.floor(stunden / 24)
  return tage === 1 ? 'gestern' : `${tage} Tagen`
}
